// LandingPage.jsx — zenithpost.ai
// Updated: removed exaggerated stats, added trusted-by logos section,
// 10-image content samples, Enterprise section, working contact form,
// policy page links

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const D = {
  bg: "#0B0C10",
  surface: "#0F1117",
  card: "#13151E",
  cardUp: "#181B27",
  border: "rgba(255,255,255,0.062)",
  borderMed: "rgba(255,255,255,0.10)",
  accent: "#6C7EF8",
  accentDim: "rgba(108,126,248,0.10)",
  accentBorder: "rgba(108,126,248,0.22)",
  success: "#34D399",
  successDim: "rgba(52,211,153,0.08)",
  warn: "#F59E0B",
  warnDim: "rgba(245,158,11,0.08)",
  purple: "#A378F8",
  textPrimary: "#EDEEF5",
  // Body copy, nav links, card descriptions — 14.8:1 contrast on bg
  textSec: "#DCE0EC",
  // Labels, tags, eyebrows, footers, meta — 9.0:1 contrast on bg
  textMuted: "#A8B0C8",
  fontDisplay: "'Bricolage Grotesque', 'Plus Jakarta Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

const G = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background:${D.bg}; color:${D.textPrimary}; font-family:${D.fontBody}; -webkit-font-smoothing:antialiased; overflow-x:hidden; }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.1); border-radius:99px; transition:background 0.2s; }
  ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.18); }

  @keyframes fadeUp    { from{opacity:0;transform:translateY(26px);filter:blur(6px)} to{opacity:1;transform:translateY(0);filter:blur(0)} }
  @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:0.32} }
  @keyframes gradMove  { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
  @keyframes slideDown { from{opacity:0;transform:translateY(-14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes marquee   { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes shimmer   { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes float     { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-9px)} }
  @keyframes scaleIn   { from{opacity:0;transform:scale(0.93) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
  @keyframes orbDrift1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(50px,-40px) scale(1.08)} 66%{transform:translate(-25px,30px) scale(0.95)} }
  @keyframes orbDrift2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-40px,35px) scale(1.06)} 66%{transform:translate(30px,-22px) scale(0.97)} }
  @keyframes orbDrift3 { 0%,100%{transform:translate(-50%,-50%)} 50%{transform:translate(-50%,-55%)} }
  @keyframes ringPulse { 0%,100%{box-shadow:0 0 0 0 rgba(108,126,248,0.4)} 60%{box-shadow:0 0 0 12px rgba(108,126,248,0)} }
  @keyframes shimmerSweep { 0%{left:-100%} 100%{left:200%} }
  @keyframes dotPop    { 0%,100%{transform:scale(1)} 50%{transform:scale(1.5)} }
  @keyframes navSlide  { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }

  .fu { animation:fadeUp 0.65s cubic-bezier(0.22,1,0.36,1) both; }
  .d1 { animation-delay:0.08s; }
  .d2 { animation-delay:0.16s; }
  .d3 { animation-delay:0.24s; }
  .d4 { animation-delay:0.32s; }
  .d5 { animation-delay:0.40s; }

  .nav { position:fixed;top:0;left:0;right:0;z-index:300;height:62px;display:flex;align-items:center;justify-content:space-between;padding:0 clamp(16px,5vw,80px);backdrop-filter:blur(28px) saturate(1.6);border-bottom:1px solid ${D.border};transition:background 0.4s ease, border-color 0.4s ease;animation:navSlide 0.5s ease both; }
  .nav-logo { display:flex;align-items:center;gap:10px;text-decoration:none;flex-shrink:0;transition:opacity 0.2s ease; }
  .nav-logo:hover { opacity:0.88; }
  .nav-mark { width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,${D.accent} 0%,${D.purple} 100%);display:flex;align-items:center;justify-content:center;font-family:${D.fontDisplay};font-weight:800;font-size:15px;color:#fff;box-shadow:0 4px 16px rgba(108,126,248,0.32);animation:float 4s ease-in-out infinite,logoGlow 3s ease infinite; }
  @keyframes logoGlow { 0%,100%{box-shadow:0 4px 16px rgba(108,126,248,0.32)} 50%{box-shadow:0 4px 30px rgba(163,123,248,0.55),0 0 50px rgba(108,126,248,0.2)} }
  .nav-name { font-family:${D.fontDisplay};font-weight:700;font-size:15px;color:${D.textPrimary};letter-spacing:-0.01em; }
  .nav-tld  { color:${D.accent}; }
  .nav-mid  { display:flex;align-items:center;gap:2px; }
  .nav-link { font-family:${D.fontBody};font-size:13.5px;font-weight:500;color:${D.textSec};background:none;border:none;text-decoration:none;padding:7px 13px;border-radius:8px;cursor:pointer;transition:color 0.2s ease, background 0.2s ease; position:relative; }
  .nav-link::after { content:''; position:absolute; bottom:4px; left:13px; right:13px; height:1.5px; background:${D.accent}; transform:scaleX(0); transform-origin:center; transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1); }
  .nav-link:hover { color:${D.textPrimary}; }
  .nav-link:hover::after { transform:scaleX(1); }
  .nav-right { display:flex;align-items:center;gap:8px; }
  .nav-login { font-family:${D.fontBody};font-size:13.5px;font-weight:600;color:${D.textSec};background:none;border:none;padding:7px 13px;border-radius:8px;cursor:pointer;transition:color 0.2s ease, background 0.2s ease; }
  .nav-login:hover { color:${D.textPrimary};background:rgba(255,255,255,0.04); }
  .nav-cta { font-family:${D.fontBody};font-size:13px;font-weight:700;color:#fff;background:${D.accent};border:none;padding:8px 18px;border-radius:9px;cursor:pointer;box-shadow:0 4px 18px rgba(108,126,248,0.3);transition:background 0.2s ease, transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s ease; }
  .nav-cta:hover { background:#7D8EFF;transform:translateY(-2px) scale(1.02);box-shadow:0 8px 28px rgba(108,126,248,0.42); }
  .nav-cta:active { transform:scale(0.97); }

  .pg { position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden; }
  .pg::before { content:'';position:absolute;width:900px;height:900px;border-radius:50%;background:radial-gradient(circle,rgba(108,126,248,0.08) 0%,transparent 68%);top:-300px;left:-200px;animation:orbDrift1 24s ease-in-out infinite;will-change:transform; }
  .pg::after  { content:'';position:absolute;width:700px;height:700px;border-radius:50%;background:radial-gradient(circle,rgba(52,211,153,0.05) 0%,transparent 68%);bottom:-200px;right:-150px;animation:orbDrift2 30s ease-in-out infinite;will-change:transform; }
  .pg-mid { position:fixed;width:600px;height:600px;border-radius:50%;z-index:0;pointer-events:none;background:radial-gradient(circle,rgba(163,123,248,0.03) 0%,transparent 70%);top:50%;left:50%;animation:orbDrift3 36s ease-in-out infinite;will-change:transform; }

  .sec { position:relative;z-index:1;padding:clamp(64px,8vw,120px) clamp(16px,5vw,80px); }
  .si  { max-width:1160px;margin:0 auto; }
  .eyebrow { display:block;font-family:${D.fontMono};font-size:10px;font-weight:600;letter-spacing:0.14em;text-transform:uppercase;color:${D.accent};margin-bottom:14px; }
  .sh { font-family:${D.fontDisplay};font-weight:800;font-size:clamp(26px,3.2vw,44px);line-height:1.1;color:${D.textPrimary};margin-bottom:16px; }
  .ss { font-size:clamp(14.5px,1.4vw,16.5px);color:${D.textSec};line-height:1.78;max-width:540px; }

  .hero { min-height:100vh;display:flex;align-items:center;padding:62px clamp(16px,5vw,80px) 0; }
  .hi   { max-width:1160px;margin:0 auto;width:100%;padding:clamp(48px,7vw,90px) 0; }
  .hero-pill { display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:99px;background:${D.accentDim};border:1px solid ${D.accentBorder};font-family:${D.fontMono};font-size:10.5px;color:${D.accent};font-weight:500;letter-spacing:0.06em;margin-bottom:28px; }
  .hero-dot  { width:5px;height:5px;border-radius:50%;background:${D.accent};animation:pulse 1.8s ease infinite;flex-shrink:0; }
  .hero-h { font-family:${D.fontDisplay};font-weight:800;font-size:clamp(40px,6vw,78px);line-height:1.03;color:${D.textPrimary};margin-bottom:22px;max-width:860px;letter-spacing:-0.015em; }
  .ha { background:linear-gradient(128deg,${D.accent} 0%,${D.purple} 40%,${D.success} 100%);background-size:220% 220%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:gradMove 5s ease infinite; }
  .hero-sub { font-size:clamp(15px,1.5vw,17.5px);color:${D.textSec};line-height:1.78;max-width:520px;margin-bottom:36px; }
  .hero-acts { display:flex;align-items:center;gap:12px;flex-wrap:wrap; }
  .btn-p { display:inline-flex;align-items:center;gap:8px;font-family:${D.fontBody};font-size:14.5px;font-weight:700;color:#fff;background:linear-gradient(135deg,${D.accent} 0%,#8B72F8 100%);padding:13px 26px;border-radius:11px;border:none;cursor:pointer;text-decoration:none;box-shadow:0 6px 26px rgba(108,126,248,0.32);transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease; position:relative; overflow:hidden; }
  .btn-p::after { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.1) 0%, transparent 50%); opacity:0; transition:opacity 0.2s ease; }
  .btn-p:hover { transform:translateY(-3px) scale(1.02); box-shadow:0 14px 38px rgba(108,126,248,0.46); }
  .btn-p:hover::after { opacity:1; }
  .btn-p:active { transform:scale(0.97); }
  .btn-s { display:inline-flex;align-items:center;gap:8px;font-family:${D.fontBody};font-size:14px;font-weight:600;color:${D.textSec};background:rgba(255,255,255,0.04);padding:13px 22px;border-radius:11px;border:1px solid ${D.border};cursor:pointer;text-decoration:none;transition:all 0.25s cubic-bezier(0.34,1.56,0.64,1); }
  .btn-s:hover { color:${D.textPrimary};border-color:rgba(255,255,255,0.18);background:rgba(255,255,255,0.07);transform:translateY(-1px);box-shadow:0 6px 20px rgba(0,0,0,0.15); }

  .srow  { display:flex;align-items:flex-end;gap:clamp(20px,3.5vw,44px);margin-top:56px;flex-wrap:wrap; }
  .sitem { display:flex;flex-direction:column;gap:4px; }
  .sval  { font-family:${D.fontDisplay};font-weight:800;font-size:clamp(22px,2.4vw,30px);color:${D.textPrimary};line-height:1; }
  .slbl  { font-family:${D.fontMono};font-size:10px;color:${D.textMuted};letter-spacing:0.09em;text-transform:uppercase; }
  .sdiv  { width:1px;height:34px;background:${D.border};flex-shrink:0; }

  .tstrip { background:${D.surface};border-top:1px solid ${D.border};border-bottom:1px solid ${D.border};padding:20px clamp(16px,5vw,80px);display:flex;align-items:center;justify-content:center;gap:clamp(14px,3vw,48px);flex-wrap:wrap; }
  .titem  { display:flex;align-items:center;gap:7px; }
  .tdot   { width:5px;height:5px;border-radius:50%;background:${D.success};animation:pulse 2.2s ease infinite; }
  .ttext  { font-family:${D.fontMono};font-size:10.5px;color:${D.textSec}; }

  /* ── TRUSTED BY LOGOS ── */
  .trusted-sec { position:relative;z-index:1;padding:clamp(40px,5vw,72px) 0;overflow:hidden;border-bottom:1px solid ${D.border}; }
  .trusted-label { font-family:${D.fontMono};font-size:10px;color:${D.textMuted};letter-spacing:0.13em;text-transform:uppercase;text-align:center;margin-bottom:28px; }
  .marquee-wrap { position:relative;overflow:hidden; }
  .marquee-wrap::before,.marquee-wrap::after { content:'';position:absolute;top:0;bottom:0;width:120px;z-index:2;pointer-events:none; }
  .marquee-wrap::before { left:0;background:linear-gradient(to right,${D.bg},transparent); }
  .marquee-wrap::after  { right:0;background:linear-gradient(to left,${D.bg},transparent); }
  .marquee-track { display:flex;gap:0;width:max-content;animation:marquee 28s linear infinite; }
  .marquee-track:hover { animation-play-state:paused; }
  .logo-slot {
  display:flex;
  align-items:center;
  justify-content:center;
  padding:0 50px;
  flex-shrink:0;
}
  .logo-pill {
  display:flex;
  align-items:center;
  justify-content:center;
  padding:18px 28px;
  min-height:90px;
  border-radius:14px;
  background:${D.card};
  border:1px solid ${D.border};
  transition:border-color 0.3s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
  position:relative; overflow:hidden;
}
  .logo-pill::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(108,126,248,0.4),transparent);opacity:0;transition:opacity 0.3s ease; }
  .logo-pill:hover { border-color:${D.accentBorder};transform:translateY(-4px);box-shadow:0 12px 30px rgba(0,0,0,0.2); }
  .logo-pill:hover::after { opacity:1; }
  .logo-icon { font-size:18px; }
  .logo-name { font-family:${D.fontDisplay};font-weight:700;font-size:13px;color:${D.textSec};white-space:nowrap; }



  /* ── ENTERPRISE SECTION ── */
  .ent-sec { position:relative;z-index:1;padding:clamp(64px,8vw,120px) clamp(16px,5vw,80px);background:linear-gradient(180deg,rgba(108,126,248,0.03) 0%,transparent 100%);border-top:1px solid ${D.border}; }
  .ent-inner { max-width:1160px;margin:0 auto; }
  .ent-grid { display:grid;grid-template-columns:1fr 1fr;gap:56px;align-items:start;margin-top:52px; }
  .ent-feats { display:grid;grid-template-columns:1fr 1fr;gap:12px; }
  .ent-feat  { display:flex;align-items:flex-start;gap:11px;padding:16px 18px;border-radius:11px;background:${D.card};border:1px solid ${D.border};transition:border-color 0.18s; }
  .ent-feat:hover { border-color:${D.accentBorder}; }
  .ent-feat-icon { font-size:18px;flex-shrink:0;margin-top:1px; }
  .ent-feat-body { display:flex;flex-direction:column;gap:2px; }
  .ent-feat-title { font-family:${D.fontDisplay};font-weight:700;font-size:13.5px;color:${D.textPrimary};line-height:1.3; }
  .ent-feat-desc  { font-size:12px;color:${D.textSec};line-height:1.58; }
  .ent-cta-col { position:sticky;top:80px; }
  .ent-cta-card { background:${D.cardUp};border:1px solid ${D.accentBorder};border-radius:20px;padding:36px;box-shadow:0 24px 64px rgba(0,0,0,0.30);position:relative;overflow:hidden; }
  .ent-cta-card::before { content:'';position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 80% 50% at 50% -10%,rgba(108,126,248,0.10) 0%,transparent 60%); }
  .ent-badge { display:inline-flex;align-items:center;gap:7px;padding:4px 12px;border-radius:99px;background:rgba(108,126,248,0.12);border:1px solid rgba(108,126,248,0.28);font-family:${D.fontMono};font-size:9px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:${D.accent};margin-bottom:18px; }

  .fgrid { display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:16px;margin-top:48px; }
  .fcard { background:${D.card};border:1px solid ${D.border};border-radius:16px;padding:26px 26px 28px;position:relative;overflow:hidden;transition:border-color 0.3s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease; }
  .fcard::before { content:''; position:absolute; inset:0; border-radius:16px; background:linear-gradient(135deg, rgba(108,126,248,0.05) 0%, transparent 50%); opacity:0; transition:opacity 0.3s ease; pointer-events:none; }
  .fcard:hover { border-color:${D.accentBorder};transform:translateY(-4px);box-shadow:0 20px 52px rgba(0,0,0,0.3), 0 0 0 1px rgba(108,126,248,0.08), 0 0 40px rgba(108,126,248,0.07); }
  .fcard:hover::before { opacity:1; }
  .fcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1.5px;background:linear-gradient(90deg,transparent,rgba(108,126,248,0.55),rgba(163,123,248,0.4),transparent);opacity:0;transition:opacity 0.3s ease; }
  .fcard:hover::after { opacity:1; }
  .ficon  { width:42px;height:42px;border-radius:11px;display:flex;align-items:center;justify-content:center;font-size:19px;margin-bottom:16px; }
  .ftitle { font-family:${D.fontDisplay};font-weight:700;font-size:15.5px;color:${D.textPrimary};margin-bottom:8px;line-height:1.3; }
  .fdesc  { font-size:13.5px;color:${D.textSec};line-height:1.72; }

  .qbar  { display:flex;align-items:flex-start;gap:14px;padding:20px 24px;border-radius:14px;background:${D.accentDim};border:1px solid ${D.accentBorder};margin-top:32px; }
  .qicon { font-size:22px;flex-shrink:0;margin-top:1px; }
  .qtext { font-size:14px;color:${D.textSec};line-height:1.72; }
  .qtext strong { color:${D.textPrimary};font-weight:600; }

  .igrid { display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px;margin-top:48px; }
  .icard { background:${D.card};border:1px solid ${D.border};border-radius:16px;padding:22px 22px 24px;position:relative;overflow:hidden;transition:border-color 0.3s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease; }
  .icard::after { content:'';position:absolute;top:0;left:0;right:0;height:1.5px;background:linear-gradient(90deg,transparent,rgba(108,126,248,0.5),transparent);opacity:0;transition:opacity 0.3s ease; }
  .icard:hover { border-color:${D.accentBorder};transform:translateY(-3px);box-shadow:0 16px 40px rgba(0,0,0,0.28), 0 0 30px rgba(108,126,248,0.07); }
  .icard:hover::after { opacity:1; }
  .ibrow  { display:flex;align-items:center;gap:9px;margin-bottom:12px; }
  .ibadge { font-family:${D.fontMono};font-size:9px;font-weight:600;letter-spacing:0.11em;text-transform:uppercase;padding:3px 8px;border-radius:5px; }
  .ititle { font-family:${D.fontDisplay};font-weight:700;font-size:14.5px;color:${D.textPrimary};margin-bottom:7px;line-height:1.3; }
  .idesc  { font-size:13px;color:${D.textSec};line-height:1.68; }
  .itags  { display:flex;flex-wrap:wrap;gap:5px;margin-top:12px; }
  .itag   { font-family:${D.fontMono};font-size:9.5px;padding:2px 7px;border-radius:4px;background:rgba(255,255,255,0.04);border:1px solid ${D.border};color:${D.textMuted}; }

  .steps { display:grid;grid-template-columns:repeat(4,1fr);gap:0;margin-top:52px;position:relative; }
  .steps::before { content:'';position:absolute;top:17px;left:calc(12.5%);right:calc(12.5%);height:1px;background:linear-gradient(90deg,transparent,${D.accentBorder},transparent);pointer-events:none; }
  .step  { padding:0 18px 0 0; }
  .step:last-child { padding-right:0; }
  .snw   { width:34px;height:34px;border-radius:50%;border:1px solid ${D.accentBorder};background:${D.accentDim};display:flex;align-items:center;justify-content:center;font-family:${D.fontMono};font-size:11px;font-weight:600;color:${D.accent};margin-bottom:18px;position:relative;z-index:1;transition:transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;animation:ringPulse 2.5s ease infinite; }
  .step:hover .snw { transform:scale(1.15);box-shadow:0 0 0 6px rgba(108,126,248,0.12); }
  .stitle { font-family:${D.fontDisplay};font-weight:700;font-size:15px;color:${D.textPrimary};margin-bottom:8px;line-height:1.3; }
  .sdesc  { font-size:13.5px;color:${D.textSec};line-height:1.65; }

  .ppills { display:flex;flex-wrap:wrap;gap:10px;margin-top:36px; }
  .ppill  { display:flex;align-items:center;gap:9px;padding:11px 18px;border-radius:10px;font-size:13.5px;font-family:${D.fontBody};font-weight:600;color:${D.textPrimary};transition:transform 0.18s; }
  .ppill:hover { transform:translateY(-1px); }

  .pgrid { display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:52px;max-width:840px;margin-left:auto;margin-right:auto; }
  .pcard { background:${D.card};border:1px solid ${D.border};border-radius:18px;padding:36px 30px;position:relative;overflow:hidden;transition:transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, border-color 0.3s ease; }
  .pcard.feat { border-color:${D.accentBorder};background:${D.cardUp};box-shadow:0 0 0 1px ${D.accentBorder},0 24px 56px rgba(0,0,0,0.28); }
  .pcard:hover { transform:translateY(-4px);box-shadow:0 24px 60px rgba(0,0,0,0.32); }
  .pcard.feat:hover { box-shadow:0 0 0 1px ${D.accentBorder}, 0 28px 64px rgba(0,0,0,0.35), 0 0 50px rgba(108,126,248,0.12); }
  .pcard-glow { position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 80% 50% at 50% -10%,rgba(108,126,248,0.09) 0%,transparent 60%); }
  .ptier  { font-family:${D.fontMono};font-size:10px;font-weight:600;letter-spacing:0.13em;text-transform:uppercase;color:${D.textMuted};margin-bottom:6px; }
  .pname  { font-family:${D.fontDisplay};font-weight:800;font-size:22px;color:${D.textPrimary};margin-bottom:4px; }
  .ptag   { font-size:13px;color:${D.textSec};margin-bottom:20px;line-height:1.5; }
  .pprow  { display:flex;align-items:baseline;gap:4px;margin-bottom:18px; }
  .pprice { font-family:${D.fontDisplay};font-weight:800;font-size:38px;color:${D.textPrimary};line-height:1;letter-spacing:-0.01em; }
  .pcurr  { font-size:19px;font-weight:700;color:${D.textSec};align-self:flex-start;margin-top:5px; }
  .pper   { font-size:13px;color:${D.textMuted}; }
  .pcustom { font-family:${D.fontDisplay};font-weight:700;font-size:26px;color:${D.textSec}; }
  .pdiv   { height:1px;background:${D.border};margin:18px 0; }
  .pfeats { display:flex;flex-direction:column;gap:10px;margin-bottom:24px; }
  .pfeat  { display:flex;align-items:flex-start;gap:9px;font-size:13.5px;color:${D.textSec};line-height:1.5; }
  .pchk   { font-size:13px;flex-shrink:0;margin-top:1px; }
  .pnote  { font-family:${D.fontMono};font-size:10px;color:${D.textMuted};text-align:center;margin-top:12px;line-height:1.6; }

  /* Enterprise contact form */
  .ent-form-wrap { position:relative; }
  .cinput { width:100%;padding:11px 14px;border-radius:9px;background:rgba(255,255,255,0.04);border:1px solid ${D.border};color:${D.textPrimary};font-family:${D.fontBody};font-size:13.5px;outline:none;transition:border-color 0.18s,box-shadow 0.18s;resize:none; }
  .cinput::placeholder { color:${D.textMuted}; }
  .cinput:focus { border-color:${D.accent};box-shadow:0 0 0 3px rgba(108,126,248,0.11); }
  .cgrid  { display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:22px; }
  .cfull  { grid-column:1/-1; }
  .cta    { min-height:96px; }
  .form-status { display:flex;align-items:center;gap:8px;padding:12px 16px;border-radius:9px;font-size:13px;margin-top:12px; }
  .form-status.success { background:rgba(52,211,153,0.10);border:1px solid rgba(52,211,153,0.24);color:#34D399; }
  .form-status.error   { background:rgba(239,68,68,0.10);border:1px solid rgba(239,68,68,0.22);color:#F87171; }
  .form-status.sending { background:${D.accentDim};border:1px solid ${D.accentBorder};color:${D.accent}; }

  .footer { padding:52px clamp(16px,5vw,80px) 36px;border-top:1px solid ${D.border};position:relative;z-index:1; }
  .fi     { max-width:1160px;margin:0 auto; }
  .ftop   { display:flex;align-items:flex-start;justify-content:space-between;gap:32px;flex-wrap:wrap;margin-bottom:40px; }
  .fbrand { max-width:276px; }
  .fbdesc { font-size:13px;color:${D.textSec};line-height:1.72;margin-top:12px; }
  .fcols  { display:flex;gap:48px;flex-wrap:wrap; }
  .fcol   { display:flex;flex-direction:column;gap:8px; }
  .fctitle { font-family:${D.fontMono};font-size:10px;color:${D.textMuted};letter-spacing:0.1em;text-transform:uppercase;margin-bottom:2px; }
  .fclink  { font-size:13px;color:${D.textSec};text-decoration:none;background:none;border:none;cursor:pointer;transition:color 0.15s;text-align:left; }
  .fclink:hover { color:${D.accent}; }
  .fbot  { display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;padding-top:24px;border-top:1px solid ${D.border}; }
  .fcopy { font-family:${D.fontMono};font-size:10.5px;color:${D.textMuted}; }
  .fstat { display:flex;align-items:center;gap:6px;font-family:${D.fontMono};font-size:10.5px;color:${D.textMuted}; }

  @media (max-width:960px) {
    .ent-grid { grid-template-columns:1fr; }
    .ent-cta-col { position:static; }
    .ent-feats { grid-template-columns:1fr; }
  }
  @media (max-width:840px) {
    .pgrid { grid-template-columns:1fr;max-width:460px; }
    .steps { grid-template-columns:1fr 1fr;gap:32px; }
    .steps::before { display:none; }
  }
  @media (max-width:600px) {
    .nav-mid { display:none; }
    .steps { grid-template-columns:1fr; }
    .cgrid { grid-template-columns:1fr; }
    .ftop  { flex-direction:column; }
    .fcols { gap:28px; }
  }

  /* ── SCROLL-REVEAL ANIMATIONS ── */
  .scroll-reveal {
    opacity: 0;
    transform: translateY(28px);
    filter: blur(5px);
    transition: opacity 0.68s cubic-bezier(0.22,1,0.36,1), transform 0.68s cubic-bezier(0.22,1,0.36,1), filter 0.68s ease;
  }
  .scroll-reveal.visible { opacity: 1; transform: translateY(0); filter: blur(0); }
  .scroll-reveal.d1 { transition-delay: 0.07s; }
  .scroll-reveal.d2 { transition-delay: 0.14s; }
  .scroll-reveal.d3 { transition-delay: 0.21s; }
  .scroll-reveal.d4 { transition-delay: 0.28s; }

  /* ── MOBILE NAV DRAWER ── */
  .mob-btn { display: none; background: none; border: 1px solid rgba(255,255,255,0.10); border-radius: 8px; padding: 7px 11px; cursor: pointer; color: ${D.textSec}; font-size: 18px; line-height: 1; align-items: center; justify-content: center; transition: border-color 0.15s; }
  .mob-btn:hover { border-color: rgba(255,255,255,0.22); }
  .mob-drawer { position: fixed; inset: 0; z-index: 400; background: rgba(11,12,16,0.97); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); padding: 0 28px 40px; transform: translateX(100%); transition: transform 0.32s cubic-bezier(0.4,0,0.2,1); display: flex; flex-direction: column; }
  .mob-drawer.open { transform: translateX(0); }
  .mob-drawer-head { display: flex; align-items: center; justify-content: space-between; height: 62px; flex-shrink: 0; border-bottom: 1px solid rgba(255,255,255,0.07); }
  .mob-drawer-close { background: none; border: none; color: ${D.textMuted}; font-size: 26px; cursor: pointer; padding: 4px; line-height: 1; }
  .mob-drawer-body { display: flex; flex-direction: column; padding-top: 8px; }
  .mob-link { display: block; font-family: ${D.fontBody}; font-size: 20px; font-weight: 600; color: ${D.textSec}; padding: 18px 0; border-bottom: 1px solid rgba(255,255,255,0.06); text-decoration: none; transition: color 0.15s; }
  .mob-link:hover { color: ${D.textPrimary}; }
  .mob-link:last-child { border-bottom: none; }
  .mob-cta-wrap { margin-top: 32px; }

  /* ── COMPREHENSIVE MOBILE ── */
  @media (max-width: 768px) {
    .mob-btn { display: flex; }
    .nav-right .nav-login { display: none; }

    .hero { padding: 62px 20px 0; min-height: auto; }
    .hi { padding: 44px 0 56px; }
    .hero-pill { font-size: 9.5px; padding: 4px 12px; }
    .hero-h { font-size: clamp(30px,8vw,50px); max-width: 100%; }
    .hero-sub { font-size: 15px; max-width: 100%; }
    .hero-acts { flex-direction: column; align-items: stretch; gap: 10px; }
    .hero-acts .btn-p, .hero-acts .btn-s { justify-content: center; width: 100%; padding: 14px 20px; }
    .srow { gap: 14px; margin-top: 28px; flex-wrap: wrap; }
    .sdiv { display: none; }
    .sval { font-size: 20px; }

    .sec { padding: 52px 20px; }
    .sh { font-size: clamp(22px,5.5vw,32px); }
    .ss { font-size: 14px; max-width: 100%; }
    .eyebrow { font-size: 9px; }

    .fgrid { grid-template-columns: 1fr; gap: 12px; margin-top: 28px; }
    .fcard { padding: 22px 20px 24px; }

    .igrid { grid-template-columns: 1fr; gap: 12px; margin-top: 28px; }
    .icard { padding: 18px 18px 20px; }

    .steps { grid-template-columns: 1fr; gap: 24px; margin-top: 32px; }
    .steps::before { display: none; }
    .step { padding: 0; }

    .ppills { gap: 7px; margin-top: 24px; }
    .ppill { font-size: 12.5px; padding: 9px 14px; }

    .pgrid { grid-template-columns: 1fr; max-width: 100%; }
    .pcard { padding: 28px 22px; }
    .pprice { font-size: 32px; }

    .tstrip { gap: 8px; padding: 14px 20px; flex-wrap: wrap; }
    .titem:nth-child(n+4) { display: none; }

    .ent-grid { grid-template-columns: 1fr; gap: 28px; }
    .ent-feats { grid-template-columns: 1fr; gap: 10px; }
    .ent-cta-card { padding: 24px 20px; }
    .ent-sec { padding: 52px 20px; }

    .qbar { flex-direction: column; gap: 8px; align-items: flex-start; }

    .trusted-label { font-size: 9px; }

    .footer { padding: 44px 20px 28px; }
    .ftop { flex-direction: column; gap: 28px; }
    .fcols { flex-wrap: wrap; gap: 24px; }
    .fcol { min-width: 120px; }
  }

  @media (max-width: 480px) {
    .hero-h { font-size: 28px; line-height: 1.08; }
    .nav-cta { font-size: 12px; padding: 7px 14px; }
    .pgrid { max-width: 100%; }
    .logo-slot { padding: 0 28px; }
    .logo-pill { padding: 14px 18px; min-height: 70px; }
    .logo-name { font-size: 11px; }
    .logo-icon { font-size: 15px; }
  }
`;

// ─── DATA ──────────────────────────────────────────────────────────────────────

const BRANDS = [
  { logo: "/logos/furbabiesai.png", name: "Furbabies.AI" },
  { logo: "/logos/dna4addiction.png", name: "DNA4Addiction" },
  { logo: "/logos/labarium.png", name: "Labarium" },
  { logo: "/logos/proove.png", name: "Proove Genomics" },
  { logo: "/logos/outcomeus.jpg", name: "Outcomeus" },
  { logo: "/logos/humanity.png", name: "Humanity" },
];

const FEATURES = [
  {
    icon: "🎯",
    bg: "rgba(108,126,248,0.11)",
    title: "Know Exactly What to Post",
    desc: "No more guessing. Our engine monitors signals across search, social, and news — surfacing the topics gaining real momentum right now in your market.",
  },
  {
    icon: "✍️",
    bg: "rgba(52,211,153,0.08)",
    title: "Social Media Content Worth Reading",
    desc: "Not templates. Not filler. zenithpost.ai writes captions, threads, blogs, and newsletters calibrated to your brand voice — content your audience actually stops scrolling for.",
  },
  {
    icon: "📸",
    bg: "rgba(245,158,11,0.08)",
    title: "Images Ready for Every Platform",
    desc: "Every content package includes AI-generated post images with captions and visuals — use them interchangeably, post when you're ready.",
  },
  {
    icon: "🔗",
    bg: "rgba(163,120,248,0.1)",
    title: "Real Citations, Included",
    desc: "Every trend search surfaces real, cited sources with title and URL — packaged alongside your content so you can reference them freely or swap them into your posts.",
  },
  {
    icon: "📦",
    bg: "rgba(52,211,153,0.08)",
    title: "One Download. You Post on Your Terms.",
    desc: "Download a clean ZIP with everything — posts, blogs, newsletters, images, and citations. You stay in full control of when and where content goes live. We just do the heavy lifting.",
  },
  {
    icon: "🧠",
    bg: "rgba(108,126,248,0.11)",
    title: "Built Around Your Brand, Not a Template",
    desc: "Your tone, values, audience language, platform preferences, and hard guardrails are loaded into the engine. Every output sounds like you wrote it on your best day.",
  },
];

const INTEL = [
  {
    icon: "🚀",
    badge: "Breakout Detection",
    bc: "rgba(108,126,248,0.12)",
    bclr: "#6C7EF8",
    title: "Know which topics are actually breaking out",
    desc: "The Breakout Score fuses velocity, virality, and freshness into a single composite — classifying each trend as 🚀 BREAKOUT, ⚡ Emerging, or normal. You see the signal, not the noise.",
    tags: ["Breakout Score", "Trend Label", "7d Growth Forecast"],
  },
  {
    icon: "⏳",
    badge: "Timing Window",
    bc: "rgba(52,211,153,0.09)",
    bclr: "#34D399",
    title: "Post before the window closes",
    desc: "Freshness and Longevity signals tell you exactly how recent the spike is and how long it'll hold. Publish at peak momentum — not in the tail when everyone else has already moved on.",
    tags: ["Freshness", "Longevity", "Velocity"],
  },
  {
    icon: "🎯",
    badge: "White Space",
    bc: "rgba(163,120,248,0.1)",
    bclr: "#A378F8",
    title: "Find the gaps your competitors already missed",
    desc: "The Opportunity Gap score measures the ratio of momentum to saturation. High gap means you can position on a topic early, before it becomes saturated. Low gap means it's already crowded.",
    tags: ["Opportunity Gap", "Saturation", "Creator Opportunity"],
  },
  {
    icon: "💡",
    badge: "Emotional Signal",
    bc: "rgba(245,158,11,0.09)",
    bclr: "#F59E0B",
    title: "Understand what's driving the conversation",
    desc: "Emotional Intensity and Virality scoring reveal the psychological charge behind each trend — fear, hype, excitement, anger. Your content taps into what's already moving people to engage.",
    tags: ["Emotional Intensity", "Virality", "Monetization Potential"],
  },
  {
    icon: "📡",
    badge: "Multi-Source Intelligence",
    bc: "rgba(52,211,153,0.09)",
    bclr: "#34D399",
    title: "Search + social + news. All at once.",
    desc: "Each score draws from three simultaneous signal streams: search spike strength, social platform velocity, and news amplification intensity — weighted and fused into one intelligence score.",
    tags: ["Search Spike", "Social Velocity", "News Amplification"],
  },
];

const STEPS = [
  {
    num: "01",
    title: "Enter your market",
    desc: "Type your niche. The engine starts scanning live signals immediately — surfacing what's gaining momentum right now.",
  },
  {
    num: "02",
    title: "Review the intelligence",
    desc: "See topics scored across 16 signals. One composite score per trend tells you exactly which ones are worth your time and which aren't.",
  },
  {
    num: "03",
    title: "Generate your content",
    desc: "One click writes brand-calibrated social media content for 6 platforms simultaneously — posts, long-form blogs, newsletters.",
  },
  {
    num: "04",
    title: "Download. You post it.",
    desc: "Everything lands in a clean ZIP — posts, images, citations included. Post when you want, through your own tools, on your own schedule.",
  },
];

const STD_FEATS = [
  "10 trend intelligence searches per month",
  "4 full content generations per month",
  "Facebook, Instagram, LinkedIn, Twitter/X, Blog & Newsletter",
  "AI-generated post images — every content set",
  "Cited sources per topic, included with every set",
  "16-signal composite opportunity scoring",
  "Brand voice calibration & guardrails",
  "ZIP export — posts, images, citations together",
];

const ENT_FEATS = [
  "Everything in Standard",
  "84 content generations per month",
  "210 trend intelligence searches per month",
  "Dedicated account support",
  "White-label content output",
];


const PLATFORMS = [
  {
    label: "Instagram",
    icon: "📸",
    bg: "rgba(251,113,133,0.09)",
    br: "rgba(251,113,133,0.22)",
  },
  {
    label: "Facebook",
    icon: "📘",
    bg: "rgba(96,165,250,0.08)",
    br: "rgba(96,165,250,0.22)",
  },
  {
    label: "LinkedIn",
    icon: "💼",
    bg: "rgba(108,126,248,0.09)",
    br: "rgba(108,126,248,0.22)",
  },
  {
    label: "Twitter / X",
    icon: "𝕏",
    bg: "rgba(255,255,255,0.04)",
    br: "rgba(255,255,255,0.10)",
  },
  {
    label: "Blog",
    icon: "📝",
    bg: "rgba(52,211,153,0.08)",
    br: "rgba(52,211,153,0.22)",
  },
  {
    label: "Newsletter",
    icon: "📬",
    bg: "rgba(245,158,11,0.08)",
    br: "rgba(245,158,11,0.22)",
  },
];

const ADDON_OPTIONS = [
  "Social Auto-Publisher — $178/month",
  "Compliance Engine — $78/month (Standard) · $298/month (Enterprise)",
  "Content Intelligence Suite — $198/month",
  "Freestyle Run — $48/month",
];

function EnterpriseForm({ onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;
    setStatus("sending");
    try {
      const res = await fetch(`${BASE_URL}/api/contact/enterprise`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) setStatus("success");
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  if (status === "success") return (
    <div style={{ textAlign: "center", padding: "32px 0" }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>✓</div>
      <h3 style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, marginBottom: 10 }}>
        Enquiry received.
      </h3>
      <p style={{ fontSize: 14, color: D.textSec, lineHeight: 1.7 }}>
        We'll be in touch at <strong style={{ color: D.textPrimary }}>{form.email}</strong> within one business day.
      </p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ fontFamily: D.fontMono, fontSize: 10, color: D.accent, letterSpacing: "0.13em", textTransform: "uppercase", marginBottom: 8 }}>
        Enterprise enquiry
      </div>
      <h3 style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, marginBottom: 6, lineHeight: 1.2 }}>
        Get started with Enterprise.
      </h3>
      <p style={{ fontSize: 13, color: D.textSec, lineHeight: 1.7, marginBottom: 0 }}>
        Tell us about your team, platforms, and industry. We'll be in touch within one business day.
      </p>
      <div className="cgrid">
        <input className="cinput" placeholder="Your name *" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
        <input className="cinput" placeholder="Work email *" type="email" required value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
        <input className="cinput cfull" placeholder="Company name *" required value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} />
        <textarea className="cinput cfull cta" placeholder="Tell us about your platforms, industry, and what you're looking to achieve…" value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} />
      </div>
      {status === "sending" && <div className="form-status sending">⏳ Sending your enquiry…</div>}
      {status === "error" && <div className="form-status error">⚠️ Something went wrong. Email us at <strong>info@zenithpost.ai</strong></div>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14, gap: 10, alignItems: "center" }}>
        <span style={{ fontFamily: D.fontMono, fontSize: 10, color: D.textMuted }}>info@zenithpost.ai</span>
        <button type="submit" className="btn-p" style={{ fontSize: 14, opacity: status === "sending" ? 0.6 : 1 }} disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : "Send enquiry →"}
        </button>
      </div>
    </form>
  );
}

function AddonForm({ defaultAddon, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", addon: defaultAddon || ADDON_OPTIONS[0], message: "" });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return;
    setStatus("sending");
    try {
      const res = await fetch(`${BASE_URL}/api/contact/addon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) setStatus("success");
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  if (status === "success") return (
    <div style={{ textAlign: "center", padding: "32px 0" }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>✓</div>
      <h3 style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, marginBottom: 10 }}>
        Enquiry received.
      </h3>
      <p style={{ fontSize: 14, color: D.textSec, lineHeight: 1.7 }}>
        We'll be in touch at <strong style={{ color: D.textPrimary }}>{form.email}</strong> within one business day.
      </p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ fontFamily: D.fontMono, fontSize: 10, color: D.accent, letterSpacing: "0.13em", textTransform: "uppercase", marginBottom: 8 }}>
        Add-on enquiry
      </div>
      <h3 style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 20, color: D.textPrimary, marginBottom: 6, lineHeight: 1.2 }}>
        Add to your plan.
      </h3>
      <p style={{ fontSize: 13, color: D.textSec, lineHeight: 1.7, marginBottom: 0 }}>
        Select the add-on you're interested in and fill in your details. We'll set it up within one business day.
      </p>
      <div className="cgrid">
        <select
          className="cinput cfull"
          value={form.addon}
          onChange={(e) => setForm(f => ({ ...f, addon: e.target.value }))}
          style={{ background: "rgba(255,255,255,0.04)", color: "#edeef5", cursor: "pointer" }}
        >
          {ADDON_OPTIONS.map(o => <option key={o} value={o} style={{ background: "#161820", color: "#edeef5" }}>{o}</option>)}
        </select>
        <input className="cinput" placeholder="Your name *" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
        <input className="cinput" placeholder="Work email *" type="email" required value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
        <input className="cinput cfull" placeholder="Company name *" required value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} />
        <textarea className="cinput cfull cta" placeholder="Anything else we should know? (optional)" value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} />
      </div>
      {status === "sending" && <div className="form-status sending">⏳ Sending your enquiry…</div>}
      {status === "error" && <div className="form-status error">⚠️ Something went wrong. Email us at <strong>info@zenithpost.ai</strong></div>}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14, gap: 10, alignItems: "center" }}>
        <span style={{ fontFamily: D.fontMono, fontSize: 10, color: D.textMuted }}>info@zenithpost.ai</span>
        <button type="submit" className="btn-p" style={{ fontSize: 14, opacity: status === "sending" ? 0.6 : 1 }} disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : "Send enquiry →"}
        </button>
      </div>
    </form>
  );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(ADDON_OPTIONS[0]);

  const openAddonModal = (addonLabel) => { setSelectedAddon(addonLabel); setShowAddonModal(true); };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const allBrands = [...BRANDS, ...BRANDS];

  return (
    <>
      <style>{G}</style>
      <div className="pg" />
      <div className="pg-mid" />

      {/* ── NAV ── */}
      <nav
        className="nav"
        style={{
          background: scrolled ? "rgba(11,12,16,0.97)" : "rgba(11,12,16,0.70)",
        }}
      >
        <a className="nav-logo" href="/">
          <div className="nav-mark">Z</div>
          <span className="nav-name">
            zenithpost<span className="nav-tld">.ai</span>
          </span>
        </a>
        <div className="nav-mid">
          <a className="nav-link" href="#how-it-works">
            How it works
          </a>
          <a className="nav-link" href="#intelligence">
            Intelligence
          </a>
          <a className="nav-link" href="#enterprise">
            Enterprise
          </a>
          <a className="nav-link" href="#addons">
            Add-ons
          </a>
          <a className="nav-link" href="#pricing">
            Pricing
          </a>
        </div>
        <div className="nav-right">
          <button className="nav-login" onClick={() => navigate("/login")}>
            Log in
          </button>
          <button className="nav-cta" onClick={() => navigate("/signup")}>
            Get started
          </button>
        </div>
        <button className="mob-btn" onClick={() => setMobileOpen(true)}>☰</button>
      </nav>

      {/* Mobile drawer */}
      <div className={`mob-drawer${mobileOpen ? ' open' : ''}`}>
        <div className="mob-drawer-head">
          <a className="nav-logo" href="/" onClick={() => setMobileOpen(false)}>
            <div className="nav-mark">Z</div>
            <span className="nav-name">zenithpost<span className="nav-tld">.ai</span></span>
          </a>
          <button className="mob-drawer-close" onClick={() => setMobileOpen(false)}>✕</button>
        </div>
        <div className="mob-drawer-body">
          <a className="mob-link" href="#how-it-works" onClick={() => setMobileOpen(false)}>How it works</a>
          <a className="mob-link" href="#intelligence" onClick={() => setMobileOpen(false)}>Intelligence</a>
          <a className="mob-link" href="#enterprise" onClick={() => setMobileOpen(false)}>Enterprise</a>
          <a className="mob-link" href="#pricing" onClick={() => setMobileOpen(false)}>Pricing</a>
          <div className="mob-cta-wrap">
            <button className="btn-p" style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }} onClick={() => { setMobileOpen(false); navigate('/signup'); }}>
              Start now — $48/mo →
            </button>
            <button className="nav-login" style={{ width: '100%', textAlign: 'center', padding: '12px' }} onClick={() => { setMobileOpen(false); navigate('/login'); }}>
              Log in
            </button>
          </div>
        </div>
      </div>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hi">
          <div className="hero-pill fu">
            <span className="hero-dot" />
            Used by brands posting consistently — every month
          </div>

          <h1 className="hero-h fu d1">
            Your competitors posted
            <br />
            <span className="ha">5 times this week. Have you?</span>
          </h1>

          <p className="hero-sub fu d2">
            zenithpost.ai scans what's breaking right now in your market,
            identifies the exact topics worth posting about, and writes a month
            of content across 6 platforms in your voice. One hour. Done.
          </p>

          <div className="hero-acts fu d3">
            <button className="btn-p" onClick={() => navigate("/signup")}>
              Generate my first 60 posts →
            </button>
            <a className="btn-s" href="#how-it-works">
              See how it works
            </a>
          </div>
          <p style={{ fontSize: "12px", color: D.textMuted, fontFamily: D.fontBody, marginTop: "10px", marginBottom: 0 }}>
            Cancel anytime · $48/month
          </p>

          <div className="srow fu d4">
            {[
              { v: "16", l: "Signal Metrics" },
              null,
              { v: "6", l: "Platforms" },
              null,
              { v: "Cited", l: "Sources / Topic" },
              null,
              { v: "1-Click", l: "Content ZIP" },
            ].map((s, i) =>
              s === null ? (
                <div className="sdiv" key={i} />
              ) : (
                <div className="sitem" key={i}>
                  <span className="sval">{s.v}</span>
                  <span className="slbl">{s.l}</span>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ── */}
      <div className="tstrip">
        {[
          "Trend intelligence engine",
          "Content across 6 platforms",
          "Post images included",
          "Cited sources per topic",
          "You publish. We write.",
        ].map((t, i) => (
          <div className="titem" key={i}>
            <div className="tdot" style={{ animationDelay: `${i * 0.35}s` }} />
            <span className="ttext">{t}</span>
          </div>
        ))}
      </div>

      {/* ── TRUSTED BY LOGOS ── */}
      <div className="trusted-sec">
        <p className="trusted-label">Trusted by brands and content teams</p>
        <div className="marquee-wrap">
          <div className="marquee-track">
            {allBrands.map((b, i) => (
              <div className="logo-slot" key={i}>
                <div className="logo-pill">
                  <img
                    src={b.logo}
                    alt={b.name}
                    style={{
                      maxHeight: 120,
                      maxWidth: 250,
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      opacity: 0.85,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES ── */}
      <section className="sec" id="features">
        <div className="si">
          <span className="eyebrow scroll-reveal">What zenithpost.ai does</span>
          <h2 className="sh scroll-reveal d1">
            The 3 hours you spend on
            <br />
            content every week. We do it in one.
          </h2>
          <p className="ss scroll-reveal d2">
            Most brands spend hours every week figuring out what to post and
            then struggling to write it well. zenithpost.ai handles both — what to
            write, and how to write it exceptionally.
          </p>

          <div className="qbar scroll-reveal d3">
            <span className="qicon">⭐</span>
            <p className="qtext">
              <strong>Quality is non-negotiable here.</strong> We don't generate
              content to fill a quota. Every caption, thread, and blog is
              written to a standard that survives your own editorial bar — the
              kind you read back and actually want to post.
            </p>
          </div>

          <div className="fgrid">
            {FEATURES.map((f, i) => (
              <div
                className="fcard scroll-reveal"
                key={i}
                style={{ animationDelay: `${0.06 + i * 0.06}s` }}
              >
                <div className="ficon" style={{ background: f.bg }}>
                  {f.icon}
                </div>
                <div className="ftitle">{f.title}</div>
                <div className="fdesc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="sec" id="how-it-works">
        <div className="si">
          <span className="eyebrow scroll-reveal">The workflow</span>
          <h2 className="sh scroll-reveal d1">
            From blank page to six
            <br />
            platforms. You set it up once.
          </h2>
          <p className="ss scroll-reveal d2">
            No briefs. No back-and-forth. Enter your niche, pick your trends,
            and let the engine do the work — posts, images, and sources all
            delivered in one download. Generation typically takes around an
            hour.
          </p>

          <div className="steps">
            {STEPS.map((s, i) => (
              <div
                className="step scroll-reveal"
                key={i}
                style={{ animationDelay: `${0.06 + i * 0.09}s` }}
              >
                <div className="snw">{s.num}</div>
                <div className="stitle">{s.title}</div>
                <div className="sdesc">{s.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 52 }}>
            <div
              style={{
                fontFamily: D.fontMono,
                fontSize: 10,
                color: D.textMuted,
                letterSpacing: "0.11em",
                textTransform: "uppercase",
                marginBottom: 16,
              }}
            >
              Platforms covered
            </div>
            <div className="ppills">
              {PLATFORMS.map((p) => (
                <div
                  className="ppill"
                  key={p.label}
                  style={{ background: p.bg, border: `1px solid ${p.br}` }}
                >
                  <span style={{ fontSize: 17 }}>{p.icon}</span>
                  {p.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── INTELLIGENCE ── */}
      <section
        className="sec"
        id="intelligence"
        style={{
          background: "rgba(255,255,255,0.012)",
          borderTop: `1px solid ${D.border}`,
        }}
      >
        <div className="si">
          <span className="eyebrow scroll-reveal">The intelligence engine</span>
          <h2 className="sh scroll-reveal d1" style={{ maxWidth: 580 }}>
            16 signals. One score.
            <br />
            You know exactly what's worth posting.
          </h2>
          <p className="ss scroll-reveal d2">
            Every trend in your market is scored across 16 proprietary signals —
            fused into a single composite intelligence score. You don't read
            dashboards. You read the answer.
          </p>

          <div className="igrid">
            {INTEL.map((c, i) => (
              <div
                className="icard scroll-reveal"
                key={i}
                style={{ animationDelay: `${0.05 + i * 0.07}s` }}
              >
                <div className="ibrow">
                  <span style={{ fontSize: 19 }}>{c.icon}</span>
                  <span
                    className="ibadge"
                    style={{ background: c.bc, color: c.bclr }}
                  >
                    {c.badge}
                  </span>
                </div>
                <div className="ititle">{c.title}</div>
                <div className="idesc">{c.desc}</div>
                <div className="itags">
                  {c.tags.map((t) => (
                    <span className="itag" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ADD-ONS ── */}
      <section className="sec" id="addons" style={{ borderTop: `1px solid ${D.border}` }}>
        <div className="si">
          <span className="eyebrow scroll-reveal" style={{ textAlign: "center", display: "block" }}>Power-Ups</span>
          <h2 className="sh scroll-reveal d1" style={{ textAlign: "center", margin: "0 auto 12px" }}>
            Supercharge your plan with add-ons.
          </h2>
          <p className="ss scroll-reveal d2" style={{ textAlign: "center", margin: "0 auto 52px" }}>
            Available on Standard and Enterprise. Add only what you need — billed monthly, cancel anytime.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }} className="scroll-reveal d3">

            {/* ADD-ON 1: Auto-Publishing */}
            <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: "18px", padding: "32px 28px", display: "flex", flexDirection: "column", gap: "16px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 28, right: 28, height: "1px", background: `linear-gradient(90deg, transparent, rgba(52,211,153,0.4), transparent)` }} />
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: 44, height: 44, borderRadius: "12px", background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.22)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>📡</div>
                <div>
                  <div style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.textPrimary }}>Social Auto-Publisher</div>
                  <div style={{ fontFamily: D.fontMono, fontSize: "10px", color: D.success, letterSpacing: "0.08em", textTransform: "uppercase" }}>Automation</div>
                </div>
              </div>
              <p style={{ fontSize: "14px", color: D.textSec, lineHeight: 1.7, margin: 0 }}>
                Your content runs are automatically scheduled and published directly to your social platforms — no copy-paste, no manual uploads. Once a run completes, your editorial calendar fires on its own: right platform, right time, right post. You generate once, the system ships for you.
              </p>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontFamily: D.fontBody, fontSize: "13px", color: D.textMuted, marginBottom: "8px", fontWeight: 600 }}>What you get:</div>
                {["Direct publish to Facebook, Instagram, LinkedIn", "AI-generated editorial calendar — optimal days & times", "Set it once per run. Posts go out automatically."].map((b, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                    <span style={{ color: D.success, fontSize: "13px", flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: "13px", color: D.textSec }}>{b}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontSize: "22px", fontWeight: 800, fontFamily: D.fontDisplay, color: D.textPrimary }}>$178<span style={{ fontSize: "14px", fontWeight: 500, color: D.textMuted }}>/month</span></div>
                <div style={{ fontSize: "12px", color: D.textMuted, marginTop: "2px" }}>Flat rate — Standard or Enterprise</div>
              </div>
              <button onClick={() => openAddonModal(ADDON_OPTIONS[0])} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 20px", borderRadius: "10px", background: "rgba(52,211,153,0.10)", border: "1px solid rgba(52,211,153,0.28)", color: D.success, fontFamily: D.fontBody, fontWeight: 700, fontSize: "14px", cursor: "pointer", width: "100%" }}>
                Enquire about this add-on →
              </button>
            </div>

            {/* ADD-ON 2: Compliance Engine */}
            <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: "18px", padding: "32px 28px", display: "flex", flexDirection: "column", gap: "16px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 28, right: 28, height: "1px", background: `linear-gradient(90deg, transparent, rgba(108,126,248,0.4), transparent)` }} />
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: 44, height: 44, borderRadius: "12px", background: D.accentDim, border: `1px solid ${D.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>🛡️</div>
                <div>
                  <div style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.textPrimary }}>Compliance Engine</div>
                  <div style={{ fontFamily: D.fontMono, fontSize: "10px", color: D.accent, letterSpacing: "0.08em", textTransform: "uppercase" }}>Risk Reduction</div>
                </div>
              </div>
              <p style={{ fontSize: "14px", color: D.textSec, lineHeight: 1.7, margin: 0 }}>
                Every post is reviewed against the regulatory standards that matter to your industry before you ever download it. Healthcare, finance, legal, education — the Compliance Engine flags risky language, rewrites CRITICAL violations automatically, and delivers a structured compliance report alongside your content. An automated first pass that dramatically reduces your exposure to the most common regulatory issues.
              </p>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontFamily: D.fontBody, fontSize: "13px", color: D.textMuted, marginBottom: "8px", fontWeight: 600 }}>What you get:</div>
                {["FTC, FDA, HIPAA, GDPR — industry-matched regulatory check", "CRITICAL violations rewritten automatically before delivery", "Full compliance report: passed, fixed, flagged — every run"].map((b, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                    <span style={{ color: D.accent, fontSize: "13px", flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: "13px", color: D.textSec }}>{b}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontSize: "22px", fontWeight: 800, fontFamily: D.fontDisplay, color: D.textPrimary }}>$78<span style={{ fontSize: "14px", fontWeight: 500, color: D.textMuted }}>/month</span></div>
                <div style={{ fontSize: "12px", color: D.textMuted, marginTop: "2px" }}>Standard · <span style={{ color: D.textSec, fontWeight: 600 }}>$298/month</span> on Enterprise (84 runs)</div>
              </div>
              <button onClick={() => openAddonModal(ADDON_OPTIONS[1])} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 20px", borderRadius: "10px", background: D.accentDim, border: `1px solid ${D.accentBorder}`, color: D.accent, fontFamily: D.fontBody, fontWeight: 700, fontSize: "14px", cursor: "pointer", width: "100%" }}>
                Enquire about this add-on →
              </button>
            </div>

            {/* ADD-ON 3: Content Intelligence Suite */}
            <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: "18px", padding: "32px 28px", display: "flex", flexDirection: "column", gap: "16px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 28, right: 28, height: "1px", background: `linear-gradient(90deg, transparent, rgba(163,120,248,0.4), transparent)` }} />
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: 44, height: 44, borderRadius: "12px", background: "rgba(163,120,248,0.10)", border: "1px solid rgba(163,120,248,0.22)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>🧠</div>
                <div>
                  <div style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.textPrimary }}>Content Intelligence Suite</div>
                  <div style={{ fontFamily: D.fontMono, fontSize: "10px", color: D.purple, letterSpacing: "0.08em", textTransform: "uppercase" }}>Strategic Edge</div>
                </div>
              </div>
              <p style={{ fontSize: "14px", color: D.textSec, lineHeight: 1.7, margin: 0 }}>
                Most content teams know what to post. The ones winning know why it works, where their competitors are weak, and how to make every hook land harder. The Content Intelligence Suite delivers all of this alongside every run — competitor gap analysis, alternative hooks for each post, full SEO metadata for every blog, and a strategic content brief that maps your content to the full conversion funnel.
              </p>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontFamily: D.fontBody, fontSize: "13px", color: D.textMuted, marginBottom: "8px", fontWeight: 600 }}>What you get:</div>
                {["3 alternative hook variants per post (Curiosity, Authority, FOMO)", "Competitor gap analysis — what they're missing, what you can own", "Blog SEO metadata: keyword, meta description, search intent", "Strategic content brief — funnel mapping, next angles, distribution plan"].map((b, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                    <span style={{ color: D.purple, fontSize: "13px", flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: "13px", color: D.textSec }}>{b}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontSize: "22px", fontWeight: 800, fontFamily: D.fontDisplay, color: D.textPrimary }}>$198<span style={{ fontSize: "14px", fontWeight: 500, color: D.textMuted }}>/month</span></div>
                <div style={{ fontSize: "12px", color: D.textMuted, marginTop: "2px" }}>Flat rate — Standard or Enterprise</div>
              </div>
              <button onClick={() => openAddonModal(ADDON_OPTIONS[2])} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 20px", borderRadius: "10px", background: "rgba(163,120,248,0.10)", border: "1px solid rgba(163,120,248,0.28)", color: D.purple, fontFamily: D.fontBody, fontWeight: 700, fontSize: "14px", cursor: "pointer", width: "100%" }}>
                Enquire about this add-on →
              </button>
            </div>

            {/* ADD-ON 4: Freestyle Run */}
            <div style={{ background: D.card, border: `1px solid ${D.border}`, borderRadius: "18px", padding: "32px 28px", display: "flex", flexDirection: "column", gap: "16px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 28, right: 28, height: "1px", background: `linear-gradient(90deg, transparent, rgba(251,191,36,0.4), transparent)` }} />
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: 44, height: 44, borderRadius: "12px", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.22)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>⚡</div>
                <div>
                  <div style={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", color: D.textPrimary }}>Freestyle Run</div>
                  <div style={{ fontFamily: D.fontMono, fontSize: "10px", color: "#f59e0b", letterSpacing: "0.08em", textTransform: "uppercase" }}>Flexibility</div>
                </div>
              </div>
              <p style={{ fontSize: "14px", color: D.textSec, lineHeight: 1.7, margin: 0 }}>
                Skip trend discovery entirely. Type any topic — a product launch, a campaign, a brief your client handed you — and run the full content pipeline on it directly. Everything your standard run produces, built around the topic you choose rather than what's trending.
              </p>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontFamily: D.fontBody, fontSize: "13px", color: D.textMuted, marginBottom: "8px", fontWeight: 600 }}>What you get:</div>
                {["Run the full pipeline on any topic, any time", "No trend search required — go straight to generation", "One active Freestyle Run at a time per account"].map((b, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                    <span style={{ color: "#f59e0b", fontSize: "13px", flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: "13px", color: D.textSec }}>{b}</span>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: `1px solid ${D.border}`, paddingTop: "16px" }}>
                <div style={{ fontSize: "22px", fontWeight: 800, fontFamily: D.fontDisplay, color: D.textPrimary }}>$48<span style={{ fontSize: "14px", fontWeight: 500, color: D.textMuted }}>/month</span></div>
                <div style={{ fontSize: "12px", color: D.textMuted, marginTop: "2px" }}>Flat rate — Standard or Enterprise</div>
              </div>
              <button onClick={() => openAddonModal(ADDON_OPTIONS[3])} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "11px 20px", borderRadius: "10px", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.28)", color: "#f59e0b", fontFamily: D.fontBody, fontWeight: 700, fontSize: "14px", cursor: "pointer", width: "100%" }}>
                Enquire about this add-on →
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section
        className="sec"
        id="pricing"
        style={{
          background: "rgba(255,255,255,0.012)",
          borderTop: `1px solid ${D.border}`,
        }}
      >
        <div className="si">
          <span
            className="eyebrow scroll-reveal"
            style={{ textAlign: "center", display: "block" }}
          >
            Pricing
          </span>
          <h2
            className="sh scroll-reveal d1"
            style={{ textAlign: "center", margin: "0 auto 12px" }}
          >
            Simple, transparent pricing.
          </h2>
          <p
            className="ss scroll-reveal d2"
            style={{ textAlign: "center", margin: "0 auto" }}
          >
            Start with Standard. Move to Enterprise when you need 84 runs a month, 210 trend searches, white-label output, and dedicated support.
          </p>

          <div className="pgrid scroll-reveal d3">
            {/* STANDARD */}
            <div className="pcard feat">
              <div className="pcard-glow" />
              <div className="ptier">Standard</div>
              <div className="pname">Standard</div>
              <div className="ptag">
                For brands and creators ready to move faster.
              </div>
              <div className="pprow">
                <span className="pcurr">$</span>
                <span className="pprice">48</span>
                <span className="pper">&nbsp;/ month</span>
              </div>
              <div className="pdiv" />
              <ul className="pfeats">
                {STD_FEATS.map((f, i) => (
                  <li className="pfeat" key={i}>
                    <span className="pchk" style={{ color: D.success }}>
                      ✓
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                className="btn-p"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  fontSize: 14,
                }}
                onClick={() => navigate("/signup")}
              >
                Get started →
              </button>
              <p className="pnote">
                Cancel anytime · Powered by Stripe
              </p>
            </div>

            {/* ENTERPRISE */}
            <div className="pcard">
              <div className="ptier">Enterprise</div>
              <div className="pname">Enterprise</div>
              <div className="ptag">For businesses and teams that need scale.</div>
              <div className="pprow">
                <span className="pcurr">$</span>
                <span className="pprice">1,000</span>
                <span className="pper">&nbsp;/ month</span>
              </div>
              <div className="pdiv" />
              <ul className="pfeats">
                {ENT_FEATS.map((f, i) => (
                  <li className="pfeat" key={i}>
                    <span className="pchk" style={{ color: D.accent }}>
                      ✓
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                className="btn-s"
                style={{ width: "100%", justifyContent: "center", fontSize: 14 }}
                onClick={() => setShowEnterpriseModal(true)}
              >
                Get in touch →
              </button>
              <p className="pnote">
                Contact us · Powered by Stripe
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="fi">
          <div className="ftop">
            <div className="fbrand">
              <a
                className="nav-logo"
                href="/"
                style={{ textDecoration: "none" }}
              >
                <div className="nav-mark">Z</div>
                <span className="nav-name">
                  zenithpost<span className="nav-tld">.ai</span>
                </span>
              </a>
              <p className="fbdesc">
                Stop guessing what to post. zenithpost.ai finds the trends and
                writes the content — so you ship consistently, every month.
              </p>
            </div>
            <div className="fcols">
              <div className="fcol">
                <span className="fctitle">Product</span>
                <a className="fclink" href="#how-it-works">
                  How it works
                </a>
                <a className="fclink" href="#intelligence">
                  Intelligence engine
                </a>
                <a className="fclink" href="#enterprise">
                  Enterprise
                </a>
                <a className="fclink" href="#addons">
                  Add-ons
                </a>
                <a className="fclink" href="#pricing">
                  Pricing
                </a>
              </div>
              <div className="fcol">
                <span className="fctitle">Account</span>
                <button className="fclink" onClick={() => navigate("/signup")}>
                  Create account
                </button>
                <button className="fclink" onClick={() => navigate("/login")}>
                  Log in
                </button>
              </div>
              <div className="fcol">
                <span className="fctitle">Legal</span>
                <a className="fclink" href="/privacy">
                  Privacy Policy
                </a>
                <a className="fclink" href="/terms">
                  Terms of Service
                </a>
                <a className="fclink" href="/cookies">
                  Cookie Policy
                </a>
              </div>
              <div className="fcol">
                <span className="fctitle">Contact</span>
                <a className="fclink" href="mailto:info@zenithpost.ai">
                  info@zenithpost.ai
                </a>
                <button className="fclink" onClick={() => setShowEnterpriseModal(true)}>
                  Enterprise enquiries
                </button>
                <button className="fclink" onClick={() => setShowAddonModal(true)}>
                  Add-ons enquiry
                </button>
              </div>
            </div>
          </div>
          <div className="fbot">
            <span className="fcopy">
              © {new Date().getFullYear()} zenithpost.ai — All rights reserved.
              <a href="/admin/login" style={{ marginLeft: 16, fontSize: "11px", color: D.textMuted, opacity: 0.4, textDecoration: "none" }}>Admin</a>
            </span>
            <div className="fstat">
              <div
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: D.success,
                  animation: "pulse 2.2s ease infinite",
                }}
              />
              All systems operational
            </div>
          </div>
        </div>
      </footer>

      {/* ── ENTERPRISE MODAL ── */}
      {showEnterpriseModal && (
        <div onClick={() => setShowEnterpriseModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#161820", border: "1px solid rgba(255,255,255,0.10)", borderRadius: "20px", padding: "36px 32px", width: "100%", maxWidth: "520px", position: "relative" }}>
            <button onClick={() => setShowEnterpriseModal(false)} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.06)", border: "none", color: "#6b7390", fontSize: 18, width: 32, height: 32, borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            <EnterpriseForm onClose={() => setShowEnterpriseModal(false)} />
          </div>
        </div>
      )}

      {/* ── ADD-ON MODAL ── */}
      {showAddonModal && (
        <div onClick={() => setShowAddonModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#161820", border: "1px solid rgba(255,255,255,0.10)", borderRadius: "20px", padding: "36px 32px", width: "100%", maxWidth: "520px", position: "relative" }}>
            <button onClick={() => setShowAddonModal(false)} style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.06)", border: "none", color: "#6b7390", fontSize: 18, width: 32, height: 32, borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            <AddonForm defaultAddon={selectedAddon} onClose={() => setShowAddonModal(false)} />
          </div>
        </div>
      )}
    </>
  );
}
